import React, { FC, MouseEvent, useMemo, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import classNames from 'classnames';
import qs from 'qs';

import { IFilter } from 'types/filters';
import { debounce } from 'helpers/debounce';
import { SelectFilter } from './filters/select';
import { FiltersOption } from './filters-option';
import { useSelectEvents } from 'hooks/useSelectEvents';
import { filterActive, filterDefault } from 'assets/icons';
import { SvgWrapper } from 'components/atoms/svg-wrapper';
import { getParsedSearchParams } from 'helpers/getParsedSearchParams';
import { FILTER_SELECT_OPTIONS, FILTERS_GROUPS, FiltersGroups } from 'constants/filters';

import styles from './styles.module.css';

interface IFiltersControlProps {
  filtersGroup: FiltersGroups;
}

export const FiltersControl: FC<IFiltersControlProps> = ({ filtersGroup }) => {
  const { replace } = useHistory();
  const { pathname, search } = useLocation();

  const searchParams = qs.parse(search, { ignoreQueryPrefix: true });

  const navigationPayload = getParsedSearchParams(searchParams);

  const filtersListRef = useRef<HTMLDivElement | null>(null);

  const { isMouseOver, showOptions, hideOnMouseOut } = useSelectEvents();

  const [selectedFilter, setSelectedFilter] = useState<IFilter | null>(null);

  const listClassname = classNames(styles.list, { [styles.show]: isMouseOver, [styles.hide]: !isMouseOver });

  const selectOptions = useMemo(
    () => (selectedFilter ? FILTER_SELECT_OPTIONS[filtersGroup]?.[selectedFilter.urlKey] : []),
    [selectedFilter],
  );

  const onMouseOut = () => {
    setSelectedFilter(null);
    hideOnMouseOut();
  };

  const hideOnMouseOutDebounced = useRef(debounce(onMouseOut, 500)).current;

  const onMouseEnter = () => {
    hideOnMouseOutDebounced.cancel();
    showOptions();
  };

  const applyFilter = (value: string | number) => {
    if (selectedFilter) {
      const isExist = !!navigationPayload.find(
        (param) => param.includes(selectedFilter.urlKey) && param.includes(`${value}`),
      );

      if (!isExist) {
        const params = new URLSearchParams(navigationPayload);

        params.append(selectedFilter.urlKey, String(value));
        replace(`${pathname}?${params.toString()}`);
      }
    }
  };

  const selectFilter = (option: IFilter) => (event: MouseEvent<HTMLDivElement>) => {
    event.stopPropagation();
    setSelectedFilter(option);
  };

  const setFilterValue = (value: string | number) => (event: MouseEvent<HTMLDivElement | HTMLButtonElement>) => {
    event.stopPropagation();
    applyFilter(value);
    onMouseOut();
  };

  const filterProps = { selectedFilter, offset: filtersListRef.current?.clientWidth, onMouseEnter, setFilterValue };

  return (
    <div className={styles.filtersControl} onClick={onMouseEnter} onMouseLeave={hideOnMouseOutDebounced}>
      <div className={styles.button}>
        <SvgWrapper width={36} height={36} details={isMouseOver ? filterActive : filterDefault} />
      </div>
      <div ref={filtersListRef} onMouseEnter={onMouseEnter} className={listClassname}>
        {FILTERS_GROUPS[filtersGroup].map((option, index) => (
          <FiltersOption key={index} filter={option} selectedFilter={selectedFilter} onSelect={selectFilter} />
        ))}
      </div>
      <SelectFilter options={selectOptions} {...filterProps} />
    </div>
  );
};
